import { checkCartConfigApi } from 'app/services/showApi';
import { useCallback, useEffect, useRef, useState } from 'react';
interface ICartConfigItem {
  ticket_id: string | number;
  quantity: string | number;
  date: string;
  ticketType?: string;
  paymentMethodId?: string;
}
type ICartConfig = ICartConfigItem[];
type ErrorType = 'popup' | 'field';
interface ApiError {
  message?: string;
  action?: 'popup';
  [key: string]: unknown;
}
interface CheckCartApiResponse {
  validate: boolean;
  tickets: {
    date: string;
    minTicket: number;
    maxTicket: number;
    message: string;
    ticket_id: string;
  }[];
}
type ErrorSource = 'response' | 'exception';
type CheckCartResult<T = CheckCartApiResponse> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: CheckCartApiResponse | ApiError | any;
      type?: ErrorType;
      source: ErrorSource;
    };
type CheckCartOptions<T = CheckCartApiResponse> = {
  onStart?: (params: ICartConfig) => void;
  onFinish?: (params: ICartConfig) => void;
  onSuccess?: (data: T, params: ICartConfig) => void;
  onError?: (
    error: CheckCartApiResponse | ApiError | any,
    source?: ErrorSource,
    type?: ErrorType,
    params?: ICartConfig,
  ) => void;
};
const safeCall = <T extends unknown[]>(
  fn?: (...args: T) => void,
  ...args: T
) => {
  try {
    fn?.(...args);
  } catch (e) {
    //TODO
  }
};
const buildFormData = (params: ICartConfig) => {
  const formData = new FormData();
  params.forEach((ticket, index) => {
    Object.entries(ticket).forEach(([key, value]) => {
      if (value != null && key !== 'ticketType') {
        formData.append(`tickets[${index}][${key}]`, String(value));
      }
    });
  });
  formData.append(`ticketType`, String(params[0]?.ticketType));
  return formData;
};
export const checkCartConfigService = async (
  params: ICartConfig,
): Promise<CheckCartResult> => {
  try {
    const res = (await checkCartConfigApi(buildFormData(params))) as {
      data: CheckCartApiResponse;
    };
    if (!res.data?.validate) {
      return {
        ok: false,
        error: res.data,
        type: 'field',
        source: 'response',
      };
    }
    return { ok: true, data: res.data };
  } catch (err) {
    const error = err as ApiError;
    return {
      ok: false,
      error,
      type: error?.action === 'popup' ? 'popup' : 'field',
      source: 'exception',
    };
  }
};
export const useCheckCartConfig = () => {
  const [loadingCount, setLoadingCount] = useState(0);
  const isMountedRef = useRef(true);
  const loading = loadingCount > 0;
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const checkConfig = useCallback(
    async (
      params: ICartConfig,
      options?: CheckCartOptions,
    ): Promise<CheckCartResult> => {
      if (isMountedRef.current) {
        setLoadingCount(c => c + 1);
      }
      safeCall(options?.onStart, params);
      try {
        const result = await checkCartConfigService(params);
        if (result.ok) {
          safeCall(options?.onSuccess, result.data, params);
        } else {
          safeCall(
            options?.onError,
            result.error,
            result.source,
            result.type,
            params,
          );
        }
        return result;
      } finally {
        if (isMountedRef.current) {
          setLoadingCount(c => c - 1);
        }
        safeCall(options?.onFinish, params);
      }
    },
    [],
  );
  return { checkConfig, loading };
};
